import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { StudyShare, getShareDisplayStatus, StudyShareDisplayStatus } from '../../../services/study-share.service';

@Component({
  selector: 'app-study-share-status-badge',
  templateUrl: './study-share-status-badge.component.html',
  styleUrls: ['./study-share-status-badge.component.scss'],
  standalone: false,
})
export class StudyShareStatusBadgeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() share: StudyShare | null | undefined;

  displayStatus: StudyShareDisplayStatus | 'uploaded_to_pacs' = 'active';
  relativeLabel = '';
  exactLabel = '';

  private clockSub?: Subscription;

  ngOnInit(): void {
    this.clockSub = interval(60_000).pipe(startWith(0)).subscribe(() => this.refresh());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['share']) this.refresh();
  }

  ngOnDestroy(): void {
    this.clockSub?.unsubscribe();
  }

  private refresh(): void {
    if (!this.share) return;

    if (this.share.status === 'uploaded_to_pacs') {
      this.displayStatus = 'uploaded_to_pacs';
      this.relativeLabel = '';
      this.exactLabel = this.share.uploaded_to_pacs_at
        ? `Uploaded ${this.formatExact(this.share.uploaded_to_pacs_at.toDate())}`
        : 'Uploaded to PACS';
      return;
    }

    if (!this.share.effective_expires_at) return;

    this.displayStatus = getShareDisplayStatus(this.share);
    const expiresDate = this.share.effective_expires_at.toDate();

    if (this.displayStatus === 'expired') {
      this.relativeLabel = '';
      this.exactLabel = `Expired ${this.formatExact(expiresDate)}`;
    } else {
      this.relativeLabel = this.formatRelative(expiresDate);
      this.exactLabel = `Available until ${this.formatExact(expiresDate)}`;
    }
  }

  private formatRelative(expires: Date): string {
    const ms = expires.getTime() - Date.now();
    if (ms <= 0) return 'Expiring now';
    const totalMinutes = Math.floor(ms / 60_000);
    const hours = Math.floor(totalMinutes / 60);
    const days = Math.floor(hours / 24);
    if (days >= 2) return `Expires in ${days} days`;
    if (days === 1) return 'Expires tomorrow';
    if (hours >= 1) return `Expires in ${hours}h ${totalMinutes % 60}m`;
    if (totalMinutes >= 1) return `Expires in ${totalMinutes}m`;
    return 'Expiring now';
  }

  private formatExact(d: Date): string {
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  }
}
